/* eslint-disable camelcase */
import {
  type AttributionRow,
  type AttributionSpan,
  identityForSpeaker,
  isTranscriptSource,
  normalizedSpeakerName,
  reconcileNamedSpeakerColors,
  type SpeakerIdentity,
  type TranscriptSource,
} from './artifactDerivations'

import type { ShippedAttributionResponse } from '@/api/internal'

export const NO_SPEAKER_LABEL = 'No speaker'
const UNNAMED_LABEL = /^Speaker \d+$/
const DIGITS = /^\d+$/

const PROVIDER_SOURCES = { soniox: 'Soniox clusters', chorus: 'Chorus clusters', openai: 'OpenAI' } as const

export function finalLaneLabel(servedProvider: keyof typeof PROVIDER_SOURCES | null | undefined): string {
  return `Final: user-labeled${servedProvider ? ` · from ${PROVIDER_SOURCES[servedProvider]}` : ''}`
}

export const PRIOR_LANE_LABEL = 'Prior to user-labeled'

type ParsedSpeakerKey = { id: number; source: TranscriptSource }

// Keys look like "refined:4"; anything else is treated as having no identity.
export function parseSpeakerKey(key: string | null | undefined): ParsedSpeakerKey | null {
  if (typeof key !== 'string') return null
  const split = key.lastIndexOf(':')
  if (split < 0) return null
  const source = key.slice(0, split)
  const idPart = key.slice(split + 1)
  if (!isTranscriptSource(source) || !DIGITS.test(idPart)) return null
  const id = Number(idPart)
  return Number.isSafeInteger(id) ? { id, source } : null
}

const labelFor = (labels: Record<string, string>, key: string): string | null => {
  if (!Object.prototype.hasOwnProperty.call(labels, key)) return null
  const label = labels[key]
  return typeof label === 'string' && label.trim() ? label.trim() : null
}

const namedLabel = (labels: Record<string, string>, key: string): string | null => {
  const label = labelFor(labels, key)
  return label && !UNNAMED_LABEL.test(label) ? label : null
}

const hasValidTiming = (row: { start_ms: number; end_ms: number }): boolean =>
  Number.isFinite(row.start_ms) && Number.isFinite(row.end_ms) && row.start_ms >= 0 && row.end_ms >= row.start_ms

const sameSpeaker = (a: SpeakerIdentity | null, b: SpeakerIdentity | null): boolean => {
  if (!a || !b) return a === b
  if (a.name || b.name)
    return Boolean(a.name && b.name && normalizedSpeakerName(a.name) === normalizedSpeakerName(b.name))
  return a.speakerId != null && a.speakerId === b.speakerId
}

// The record's speaker ids are the displayed revision's, so each row is compared to Final's entry with the same id.
export function shippedRecordRows(record: ShippedAttributionResponse, refinedRows: AttributionRow[]): AttributionRow[] {
  const labels = record.labels ?? {}
  const finalById = new Map(refinedRows.flatMap((row) => (row.entry_id ? [[row.entry_id, row] as const] : [])))
  const rows = (record.rows ?? []).flatMap((row, index): AttributionRow[] => {
    const parsed = parseSpeakerKey(row.speaker)
    if (!parsed || !hasValidTiming(row)) return []
    const identity = identityForSpeaker('refined', {
      id: parsed.id,
      source: parsed.source,
      name: namedLabel(labels, row.speaker),
    })
    const speaker: SpeakerIdentity = {
      ...identity,
      key: `shipped:${identity.key}`,
      label: `Prior · ${identity.timelineLabel ?? identity.label}`,
    }
    const final = finalById.get(row.id)?.speaker ?? null
    const changed = finalById.has(row.id) && !sameSpeaker(speaker, final)
    return [
      {
        entry_id: `shipped:${row.id || index}`,
        start_ms: row.start_ms,
        end_ms: row.end_ms,
        speaker,
        text: null,
        ...(changed ? { changed: true, detail: final?.timelineLabel ?? final?.label ?? NO_SPEAKER_LABEL } : {}),
      },
    ]
  })
  return reconcileNamedSpeakerColors(rows, refinedRows)
}

// Like rowsToSpans, but a changed span never merges with an unchanged one or with a different counterpart.
export function relabelerSpans(rows: AttributionRow[]): AttributionSpan[] {
  const spans: AttributionSpan[] = []
  const openByKey = new Map<string, AttributionSpan>()
  for (const row of rows) {
    const key = [row.speaker?.key ?? '', row.changed ? '1' : '0', row.detail ?? '', row.method ?? ''].join('\u0000')
    const previous = openByKey.get(key)
    if (previous && previous.end_ms >= row.start_ms) {
      previous.end_ms = Math.max(previous.end_ms, row.end_ms)
      continue
    }
    const span: AttributionSpan = {
      start_ms: row.start_ms,
      end_ms: row.end_ms,
      speaker: row.speaker,
      ...(row.method ? { method: row.method } : {}),
      ...(row.changed ? { changed: true, detail: row.detail } : {}),
    }
    spans.push(span)
    openByKey.set(key, span)
  }
  return spans
}
